import { AiChatDocumentMessagePayloadRoleEnum } from '../../enums/AiChatDocumentMessagePayloadRoleEnum';
import type { AiChatDocumentMessagePayload } from '../../interfaces/AiChatDocumentMessagePayload';

/** Options for action visibility beyond the message row itself. */
export type ShowDocumentChatMessageActionsOptions = {
  /** Hide every bubble's actions while any assistant is still Pending. */
  livePending?: boolean;
};

/**
 * Whether copy/regenerate/TTS should show (AIGrant showMessageActions parity).
 * Never for Pending/Error/Cancel. Callers also gate mid-reveal via MessageItem.
 */
export function showDocumentChatMessageActions(
  message: AiChatDocumentMessagePayload,
  options?: ShowDocumentChatMessageActionsOptions,
): boolean {
  if (options?.livePending) return false;
  if (message.role === AiChatDocumentMessagePayloadRoleEnum.User) return false;
  if (message.isError || message.status === 'Error' || message.status === 'Cancel') return false;
  if (message.status === 'Pending') return false;
  return message.content.trim().length > 0;
}

/** Mark trailing Pending assistant Cancel (composer stop / no BE cancel for document chat). */
export function cancelPendingAssistant(
  history: AiChatDocumentMessagePayload[],
): AiChatDocumentMessagePayload[] {
  const next = [...history];
  for (let i = next.length - 1; i >= 0; i -= 1) {
    const msg = next[i];
    if (
      msg &&
      msg.role === AiChatDocumentMessagePayloadRoleEnum.Assistant &&
      msg.status === 'Pending' &&
      !msg.isError
    ) {
      next[i] = {
        ...msg,
        content: '',
        status: 'Cancel',
        streamSeq: undefined,
        streamActivities: undefined,
        streamActivity: undefined,
      };
      break;
    }
  }
  return next;
}

/** Stable list key — never flip when messageId arrives (preserves streaming reveal). */
export function documentChatMessageKey(
  message: AiChatDocumentMessagePayload,
  index: number,
): string {
  if (message.clientKey) return message.clientKey;
  if (message.messageId != null) return `message-${message.messageId}-${message.role}`;
  return `message-${index}-${message.role}-${message.createdAt}`;
}

export function newDocumentChatClientKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `local-${crypto.randomUUID()}`;
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
